import { AxisCode } from '@atlas/types'
import { useEffect, useMemo, useState } from 'react'
import { useBootstrapValues, useClassificationBootstrap } from '@/hooks/useClassification'
import { useUserNames } from '@/hooks/useUserNames'
import { useActivePms } from '@/hooks/useUsers'
import { useMappingCatalogue, useWorkfrontParameters } from '@/hooks/useWorkfrontMappings'
import { notification } from '@/notifications/notification-store'
import type { DrawerContext } from './components/drawer-context'

/** A failed list load goes to the status bar too, once per failure; the list itself offers Retry. */
export function useReportLoadFailure(isError: boolean, noun: string) {
	useEffect(() => {
		if (isError) notification.error(`Could not load the ${noun}. Retry from the list.`)
	}, [isError, noun])
}

/** Who did something, by name; the id itself while the user directory loads, or for an actor it does not list. */
export function useActorName(): (userId: string) => string {
	const names = useUserNames()
	return (userId) => names.get(userId) ?? userId
}

/** The keys chosen for a bulk action, cleared once it is done. */
export function useSelection() {
	const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
	const onToggle = (key: string) => {
		const next = new Set(selected)
		if (next.has(key)) next.delete(key)
		else next.add(key)
		setSelected(next)
	}
	return { selected, onToggle, clear: () => setSelected(new Set()) }
}

/** Asset type code → its name, from the registry's `asset_type_code` axis. */
export function useAssetTypeNames(): Record<string, string> {
	const values = useBootstrapValues(AxisCode.ASSET_TYPE_CODE)
	return useMemo(() => Object.fromEntries(values.map((value) => [value.code, value.label])), [values])
}

/**
 * What the drawer offers to pick from: the catalogue, Workfront's fields, the
 * edited axis's values and geographies, asset type names, and the classification
 * editor for the axis, pre-filled with the label being mapped.
 */
export function useDrawerContext(axis?: string, label?: string): DrawerContext {
	const catalogue = useMappingCatalogue()
	const parameters = useWorkfrontParameters('')
	const bootstrap = useClassificationBootstrap()
	const axisValues = useBootstrapValues(axis ?? '')
	const geographyValues = useBootstrapValues(AxisCode.GEOGRAPHY)
	const assetTypeNames = useAssetTypeNames()
	const pms = useActivePms()
	const axisId = bootstrap.data?.axes.find((candidate) => candidate.code === axis)?.axisId
	return {
		catalogue: catalogue.data ?? [],
		parameters: (parameters.data ?? []).map((parameter) => {
			const { label: name, displayType } = parameter.attributes as { label?: string; displayType?: string }
			return {
				value: parameter.workfrontId,
				label: `${name ?? parameter.name}${displayType ? ` · ${displayType}` : ''}`,
			}
		}),
		axisValues,
		geographyValues,
		assetTypeNames,
		createValueHref: axisId
			? `/admin/classification/axes/${axisId}${label ? `?newValue=${encodeURIComponent(label)}` : ''}`
			: null,
		// Strictly the active PMs: save-time validation refuses anyone else.
		pms: (pms.data ?? []).map((user) => ({ value: user.userId, label: user.displayName })),
	}
}
